import type {
  AudioProcessorOptions,
  Track,
  TrackProcessor,
} from "livekit-client";

import type { NoiseSuppresionState } from "@mutiny/state/stores/Voice";

/**
 * Parameters understood by the gate worklet (see voiceGate.worklet.js).
 */
export interface VoiceGateParams {
  mode: "auto" | "manual" | "off";
  inputMode: "vad" | "ptt";
  pttActive: boolean;
  manualThresholdDb: number;
}

/**
 * Live meter snapshot posted by the gate worklet (~20 Hz).
 */
export interface VoiceInputMeter {
  /** denoised (pre-gate) level, dBFS RMS */
  levelDb: number;
  peakDb: number;
  /** transmitted (post-gate) level, dBFS RMS */
  outputDb: number;
  /** tracked noise floor (dBFS), null until known */
  floorDb: number | null;
  /** effective open threshold (dBFS) */
  thresholdDb: number;
  open: boolean;
  gain: number;
}

/**
 * Which denoiser is actually running on the outgoing microphone.
 */
export type ActiveDenoiser =
  | "studio" // DeepFilterNet3
  | "enhanced" // RNNoise
  | "browser" // Chromium/WebRTC noiseSuppression constraint
  | "none";

export interface VoiceProcessingStatus {
  /** requested mode from settings */
  requested: NoiseSuppresionState;
  /** what is actually running */
  active: ActiveDenoiser;
  /** set when the requested denoiser failed and we fell back */
  fallbackFrom?: "studio" | "enhanced";
  /** gate (VAD or push-to-talk) is in the outgoing path */
  gate: boolean;
  /** pipeline is being (re)built */
  pending: boolean;
  error?: string;
}

export interface VoiceProcessorOptions {
  denoiser: NoiseSuppresionState;
  /** DeepFilterNet3 attenuation level 0..100 */
  level: number;
  gate: VoiceGateParams;
  /** RNNoise worklet/wasm base URL (empty = library default CDN) */
  rnnoiseBaseUrl?: string;
  onMeter?: (meter: VoiceInputMeter) => void;
  onStatus?: (status: VoiceProcessingStatus) => void;
}

const DFN_SAMPLE_RATE = 48000;
const HIGHPASS_HZ = 80;
const GATE_PROCESSOR_NAME = "mutiny-voice-gate";
const RNNOISE_DEFAULT_URL =
  "https://cdn.jsdelivr.net/gh/dadadah/livekit-rnnoise-processor@860d8053d4917389dfdebb20d88d0bb6ce950bda/dist/";

/**
 * DeepFilterNet3 wasm + model are vendored same-origin by
 * scripts/fetchDeepFilterAssets.mjs: the library's default CDN only allows
 * the https://mezon.ai origin (CORS), so it can't be used from Mutiny.
 */
export const DEEPFILTER_ASSET_URL = (
  (import.meta.env.VITE_DEEPFILTER_ASSET_URL as string | undefined) ||
  `${import.meta.env.BASE_URL}deepfilternet3`
).replace(/\/$/, "");

/**
 * Emitted by Vite as a hashed static asset (plain JS AudioWorklet module).
 */
const GATE_WORKLET_URL = new URL("./voiceGate.worklet.js", import.meta.url)
  .href;

/**
 * crypto/TextDecoder polyfill + init reporting for the DeepFilterNet3
 * worklet (see deepFilterShim.worklet.js for why this is required).
 */
const DFN_SHIM_URL = new URL("./deepFilterShim.worklet.js", import.meta.url)
  .href;

const DFN_INIT_TIMEOUT_MS = 5000;

/**
 * Resolve once the DeepFilterNet3 worklet reports whether its model loaded;
 * reject if it failed (the library would otherwise silently pass audio
 * through unfiltered).
 */
function waitForDeepFilterInit(node: AudioWorkletNode) {
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("DeepFilterNet3 worklet did not report init")),
      DFN_INIT_TIMEOUT_MS,
    );
    node.port.addEventListener("message", function onMessage(event) {
      if (event.data?.type !== "mutiny-dfn-init") return;
      clearTimeout(timer);
      node.port.removeEventListener("message", onMessage);
      if (event.data.ok) resolve();
      else reject(new Error("DeepFilterNet3 model failed to initialise"));
    });
    node.port.start();
  });
}

type DeepFilterCore = {
  initialize(): Promise<void>;
  createAudioWorkletNode(ctx: AudioContext): Promise<AudioWorkletNode>;
};

/**
 * One DeepFilterNet3 core per page: it holds the compiled wasm module and
 * the model bytes, so re-applying the pipeline never refetches the ~8 MB
 * model. We never call core.destroy() (that would drop the cached assets);
 * the per-context worklet nodes are torn down by the processor instead.
 */
let deepFilterCore: Promise<DeepFilterCore> | undefined;

function loadDeepFilterCore(): Promise<DeepFilterCore> {
  if (!deepFilterCore) {
    deepFilterCore = import("deepfilternet3-noise-filter")
      .then(async (mod) => {
        const base = new URL(DEEPFILTER_ASSET_URL + "/", location.href);
        const core = new mod.DeepFilterNet3Core({
          sampleRate: DFN_SAMPLE_RATE,
          noiseReductionLevel: 80,
        }) as unknown as DeepFilterCore & {
          assetLoader: {
            getAssetUrls(): { wasm: string; model: string };
          };
        };
        // vendored layout (see scripts/fetchDeepFilterAssets.mjs)
        core.assetLoader.getAssetUrls = () => ({
          wasm: new URL("df_bg.wasm", base).href,
          model: new URL("DeepFilterNet3_onnx.tgz", base).href,
        });
        await core.initialize();
        return core;
      })
      .catch((err) => {
        // allow a later retry (e.g. transient network failure)
        deepFilterCore = undefined;
        throw err;
      });
  }
  return deepFilterCore;
}

let rnnoiseWasm: Promise<ArrayBuffer> | undefined;

function loadRnnoiseWasm(baseUrl: string): Promise<ArrayBuffer> {
  if (!rnnoiseWasm) {
    rnnoiseWasm = fetch(baseUrl + "rnnoise.wasm")
      .then((resp) => {
        if (!resp.ok) throw new Error(`rnnoise.wasm: HTTP ${resp.status}`);
        return resp.arrayBuffer();
      })
      .catch((err) => {
        rnnoiseWasm = undefined;
        throw err;
      });
  }
  return rnnoiseWasm;
}

/**
 * Start fetching/compiling the model for a denoiser ahead of use (e.g. while
 * the call is connecting) so the processor attaches without a noisy gap.
 */
export function preloadDenoiser(
  denoiser: NoiseSuppresionState,
  rnnoiseBaseUrl?: string,
) {
  const load =
    denoiser === "studio"
      ? loadDeepFilterCore()
      : denoiser === "enhanced"
        ? loadRnnoiseWasm(rnnoiseBaseUrl || RNNOISE_DEFAULT_URL)
        : undefined;
  return load?.then(
    () => true,
    () => false,
  );
}

/**
 * Whether a denoiser/gate pipeline is needed at all for these options.
 */
export function needsVoiceProcessor(
  opts: Pick<VoiceProcessorOptions, "denoiser" | "gate">,
) {
  return (
    opts.denoiser === "studio" ||
    opts.denoiser === "enhanced" ||
    opts.gate.inputMode === "ptt" ||
    opts.gate.mode !== "off"
  );
}

/**
 * Single composite LiveKit audio TrackProcessor:
 *
 *   mic track -> MediaStreamSource -> [DeepFilterNet3 | RNNoise] -> gate -> MediaStreamDestination
 *
 * LiveKit allows one processor per track, so denoising, voice-activity
 * gating and push-to-talk all live in one Web Audio graph owned by one
 * AudioContext that is closed on destroy().
 */
export class MutinyVoiceProcessor implements TrackProcessor<
  Track.Kind.Audio,
  AudioProcessorOptions
> {
  readonly name = "mutiny-voice-processor";
  processedTrack?: MediaStreamTrack;

  #opts: VoiceProcessorOptions;
  #ctx?: AudioContext;
  #source?: MediaStreamAudioSourceNode;
  #highpass?: BiquadFilterNode;
  #denoiseNode?: AudioWorkletNode;
  #gateNode?: AudioWorkletNode;
  #fallbackGain?: GainNode;
  #destination?: MediaStreamAudioDestinationNode;
  #status: VoiceProcessingStatus;
  #destroyed = false;

  constructor(opts: VoiceProcessorOptions) {
    this.#opts = { ...opts, gate: { ...opts.gate } };
    this.#status = {
      requested: opts.denoiser,
      active: "none",
      gate: true,
      pending: true,
    };
  }

  static isSupported() {
    return (
      typeof AudioContext !== "undefined" &&
      typeof AudioWorkletNode !== "undefined"
    );
  }

  get status(): VoiceProcessingStatus {
    return this.#status;
  }

  get audioContext() {
    return this.#ctx;
  }

  #setStatus(patch: Partial<VoiceProcessingStatus>) {
    this.#status = { ...this.#status, ...patch };
    this.#opts.onStatus?.(this.#status);
  }

  async init(opts: AudioProcessorOptions) {
    if (!opts.track) throw new Error("MutinyVoiceProcessor: missing track");
    this.#setStatus({ pending: true });

    const ctx = this.#createContext();
    this.#ctx = ctx;
    if (ctx.state !== "running") {
      await ctx.resume().catch(() => undefined);
    }

    try {
      // gate first: it is the pipeline's only hard requirement
      await ctx.audioWorklet.addModule(GATE_WORKLET_URL);
      const gate = new AudioWorkletNode(ctx, GATE_PROCESSOR_NAME, {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        processorOptions: { params: this.#opts.gate, metering: true },
      });
      gate.port.onmessage = (event) => {
        if (event.data?.type === "meter")
          this.#opts.onMeter?.(event.data.meter);
      };
      this.#gateNode = gate;
      gate.addEventListener("processorerror", () => {
        if (this.#destroyed || this.#gateNode !== gate || !this.#destination)
          return;
        // Disconnect the crashed node and route through an explicit PTT-safe
        // fallback. The gain starts closed before it enters the sender path.
        const fallback = ctx.createGain();
        fallback.gain.value =
          this.#opts.gate.inputMode === "ptt" && !this.#opts.gate.pttActive
            ? 0
            : 1;
        this.#highpass?.disconnect();
        gate.disconnect();
        // Keep the denoiser when healthy; only replace the failed gate.
        if (this.#denoiseNode) {
          this.#denoiseNode.disconnect();
          this.#highpass?.connect(this.#denoiseNode);
        }
        (this.#denoiseNode ?? this.#highpass)?.connect(fallback);
        fallback.connect(this.#destination);
        this.#fallbackGain = fallback;
        this.#setStatus({
          gate: false,
          active: this.#denoiseNode ? this.#status.active : "none",
          error:
            "Voice gate crashed; transmitting with push-to-talk safety fallback (automatic gating unavailable)",
        });
      });

      const denoise = await this.#createDenoiser(ctx);
      this.#denoiseNode = denoise;
      denoise?.addEventListener("processorerror", () => {
        // Worklet init succeeded but its DSP crashed later. Keep the sender live
        // by routing the high-pass directly to the gate, and expose the fallback.
        if (this.#destroyed || this.#denoiseNode !== denoise) return;
        this.#highpass?.disconnect();
        denoise.disconnect();
        this.#highpass?.connect(this.#fallbackGain ?? gate);
        this.#setStatus({
          active: "none",
          fallbackFrom:
            this.#opts.denoiser === "studio" ? "studio" : "enhanced",
          error:
            "Noise suppressor worklet crashed; transmitting without denoising",
        });
      });

      const destination = ctx.createMediaStreamDestination();
      destination.channelCount = 1;
      this.#destination = destination;

      // Voice high-pass: removes rumble/hum below the voice band. The ML
      // denoisers leave sub-100 Hz energy largely untouched, and without it
      // fan rumble keeps the noise floor (and the gate) high. Chromium's APM
      // only applies its own high-pass when echo cancellation is on.
      const highpass = ctx.createBiquadFilter();
      highpass.type = "highpass";
      highpass.frequency.value = HIGHPASS_HZ;
      highpass.Q.value = Math.SQRT1_2;
      this.#highpass = highpass;

      highpass.connect(denoise ?? gate);
      if (denoise) denoise.connect(gate);
      gate.connect(destination);
      this.#connectSource(opts.track);

      this.processedTrack = destination.stream.getAudioTracks()[0];
      if (this.#destroyed) {
        await this.destroy();
        return;
      }
      this.#setStatus({ pending: false });
    } catch (error) {
      await this.destroy();
      this.#setStatus({
        pending: false,
        gate: false,
        active: "none",
        error: String(error),
      });
      throw error;
    }
  }

  async restart(opts: AudioProcessorOptions) {
    // LiveKit calls this when the underlying mic track is replaced
    // (device switch, unmute after stopOnMute, track recovery).
    if (!this.#ctx || !opts.track) return this.init(opts);
    this.#connectSource(opts.track);
  }

  async destroy() {
    this.#destroyed = true;
    try {
      this.#gateNode?.port.postMessage({ type: "destroy" });
      this.#gateNode?.port.close();
      if (this.#denoiseNode) {
        // RNNoise frees its wasm heap on DESTROY; DeepFilter ignores it
        this.#denoiseNode.port.postMessage({ message: "DESTROY" });
        this.#denoiseNode.port.close();
      }
      this.#source?.disconnect();
      this.#highpass?.disconnect();
      this.#denoiseNode?.disconnect();
      this.#gateNode?.disconnect();
      this.#fallbackGain?.disconnect();
      this.#destination?.disconnect();
    } catch {
      // ignore disconnect errors
    }
    const ctx = this.#ctx;
    this.#ctx = undefined;
    this.#source = undefined;
    this.#highpass = undefined;
    this.#denoiseNode = undefined;
    this.#gateNode = undefined;
    this.#fallbackGain = undefined;
    this.#destination = undefined;
    this.processedTrack = undefined;
    if (ctx && ctx.state !== "closed") {
      await ctx.close().catch(() => undefined);
    }
  }

  /**
   * Live-update gate parameters (mode, threshold, push-to-talk) without
   * rebuilding the graph.
   */
  setGateParams(params: Partial<VoiceGateParams>) {
    Object.assign(this.#opts.gate, params);
    if (this.#fallbackGain) {
      this.#fallbackGain.gain.value =
        this.#opts.gate.inputMode === "ptt" && !this.#opts.gate.pttActive
          ? 0
          : 1;
    }
    this.#gateNode?.port.postMessage({ type: "params", params });
  }

  /**
   * Live-update the DeepFilterNet3 attenuation level (0..100).
   */
  setSuppressionLevel(level: number) {
    this.#opts.level = level;
    if (this.#status.active === "studio") {
      this.#denoiseNode?.port.postMessage({
        type: "SET_SUPPRESSION_LEVEL",
        value: Math.max(0, Math.min(100, Math.floor(level))),
      });
    }
  }

  /** Denoiser requested at construction */
  get denoiser() {
    return this.#opts.denoiser;
  }

  #createContext() {
    const wantsModel =
      this.#opts.denoiser === "studio" || this.#opts.denoiser === "enhanced";
    try {
      return new AudioContext({
        latencyHint: "interactive",
        // both models are trained on 48 kHz audio
        sampleRate: wantsModel ? DFN_SAMPLE_RATE : undefined,
      });
    } catch {
      return new AudioContext({ latencyHint: "interactive" });
    }
  }

  #connectSource(track: MediaStreamTrack) {
    const ctx = this.#ctx!;
    this.#source?.disconnect();
    const source = ctx.createMediaStreamSource(new MediaStream([track]));
    source.connect(this.#highpass!);
    this.#source = source;
  }

  async #createDenoiser(
    ctx: AudioContext,
  ): Promise<AudioWorkletNode | undefined> {
    const requested = this.#opts.denoiser;
    if (requested === "disabled" || requested === "browser") {
      this.#setStatus({
        active: requested === "browser" ? "browser" : "none",
      });
      return undefined;
    }

    const modelRate = ctx.sampleRate === DFN_SAMPLE_RATE;

    if (requested === "studio") {
      try {
        if (!modelRate) {
          throw new Error(
            `AudioContext sample rate ${ctx.sampleRate} Hz is not 48 kHz`,
          );
        }
        const core = await loadDeepFilterCore();
        await ctx.audioWorklet.addModule(DFN_SHIM_URL);
        const node = await core.createAudioWorkletNode(ctx);
        try {
          await waitForDeepFilterInit(node);
        } catch (err) {
          node.disconnect();
          node.port.close();
          throw err;
        }
        node.port.postMessage({
          type: "SET_SUPPRESSION_LEVEL",
          value: Math.max(0, Math.min(100, Math.floor(this.#opts.level))),
        });
        this.#setStatus({ active: "studio", fallbackFrom: undefined });
        return node;
      } catch (err) {
        console.error("DeepFilterNet3 failed, falling back to RNNoise:", err);
        this.#setStatus({ error: String((err as Error)?.message ?? err) });
        const fallback = await this.#createRnnoise(ctx).catch((err2) => {
          console.error("RNNoise fallback failed:", err2);
          return undefined;
        });
        this.#setStatus({
          active: fallback ? "enhanced" : "none",
          fallbackFrom: "studio",
        });
        return fallback;
      }
    }

    // enhanced
    try {
      const node = await this.#createRnnoise(ctx);
      this.#setStatus({ active: "enhanced", fallbackFrom: undefined });
      return node;
    } catch (err) {
      console.error("RNNoise failed, continuing without denoiser:", err);
      this.#setStatus({
        active: "none",
        fallbackFrom: "enhanced",
        error: String((err as Error)?.message ?? err),
      });
      return undefined;
    }
  }

  async #createRnnoise(ctx: AudioContext) {
    if (ctx.sampleRate !== DFN_SAMPLE_RATE) {
      throw new Error(`RNNoise needs 48 kHz, context is ${ctx.sampleRate} Hz`);
    }
    const baseUrl = this.#opts.rnnoiseBaseUrl || RNNOISE_DEFAULT_URL;
    const [wasm] = await Promise.all([
      loadRnnoiseWasm(baseUrl),
      ctx.audioWorklet.addModule(new URL(baseUrl + "DenoiserWorklet.js").href),
    ]);
    return new AudioWorkletNode(ctx, "DenoiserWorklet", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      // the worklet takes ownership of the buffer, so hand it a copy
      processorOptions: { rnnoiseBuffer: wasm.slice(0) },
    });
  }
}
